/**
 * AuthClient module
 * Provides authentication services for the application using Microsoft Authentication Library (MSAL)
 * Uses the singleton MSAL instance from the auth module.
 */

import {
  InteractionRequiredAuthError,
  SilentRequest,
  RedirectRequest,
  AuthenticationResult,
} from '@azure/msal-browser';

import { scopes } from '../config/MSALConfig';
import { msalInstance } from '../auth/AuthWrapper';
import { TelemetryClient } from '../telemetry/TelemetryClient';

import { FeatureName, TelemetryDimensions } from '../telemetry/TelemetryConstants';
import { TelemetryConfig } from '../telemetry/TelemetryConfig';


/**
 * Get an access token for the specified scopes
 * @param scopeArray - Array of scopes to request access for
 * @returns Promise that resolves to the access token or null if authentication fails
 */
export const getAccessTokenForScope = async (scopeArray: string[]): Promise<string | null> => {
  // Resolve the account once so the silent and redirect requests use the same account
  const activeAccount = msalInstance.getActiveAccount();
  const accounts = msalInstance.getAllAccounts();
  const resolvedAccount = activeAccount || (accounts && accounts.length > 0 ? accounts[0] : undefined);

  try {
    const telemetryInstance = TelemetryClient.getInstance(TelemetryConfig);

    // If no accounts, authentication is required
    if (!resolvedAccount) {
      telemetryInstance?.trackFeatureUsage({
        featureName: FeatureName.Authentication,
        action: 'LoginRequired',
        componentName: 'AuthClient',
      });

      return null;
    }

    // Prepare silent token request
    const accessTokenRequest: SilentRequest = {
      scopes: scopeArray,
      account: resolvedAccount,
    };
    
    // Try to get token silently
    const accessTokenResponse: AuthenticationResult = await msalInstance.acquireTokenSilent(accessTokenRequest);
    return accessTokenResponse.accessToken;

  } catch (error) {
    const telemetryInstance = TelemetryClient.getInstance(TelemetryConfig);
    
    // If interaction required, redirect to login
    if (error instanceof InteractionRequiredAuthError) {
      telemetryInstance?.trackFeatureUsage({
        featureName: FeatureName.Authentication,
        action: 'InteractiveLoginRequired',
        componentName: 'AuthClient',
      });
      
      const accessTokenRequest: RedirectRequest = {
        scopes: scopeArray,
        account: resolvedAccount,
      };
      
      await msalInstance.acquireTokenRedirect(accessTokenRequest);
      return null;
    }
    
    // Track other errors
    telemetryInstance?.trackError(error instanceof Error ? error : new Error(String(error)), {
      [TelemetryDimensions.Feature]: FeatureName.Authentication,
    });
    
    return null;
  }
};

/**
 * Get an access token using the default scopes from MSALConfig
 * @returns Promise that resolves to the access token or null if authentication fails
 */
export const getAccessToken = async (): Promise<string | null> => {
  return getAccessTokenForScope(scopes);
};

